import { h, render, JSX, ComponentType } from "preact";

import Toast from "../../library/v2/Toast";
import { ToastVariant } from "../enums";

type PendingToast = {
    component: JSX.Element;
    target: PendingToastTarget;
    duration: number | undefined;
};

type ToastStoreState = {
    source: string | null;
    text: string | null;
    container: Element | null;
    visible: boolean;
    removeTimeout: number | null;
    pendingToast: PendingToast | null;
};

const ToastStore: ToastStoreState = {
    source: null,
    text: null,
    container: null,
    visible: false,
    removeTimeout: null,
    pendingToast: null
};

enum PendingToastTarget {
    Dashboard = "toast/dashboard",
    Space = "toast/space"
}

const TOAST_SHOW_DELAY = 50;
const TOAST_DEFAULT_DURATION = 4000; // ms

function removeToast(toastContainer: Element): void {
    toastContainer.addEventListener("transitionend", () => {
        render(null, toastContainer);
        toastContainer.remove();
        ToastStore.visible = false;
    });

    toastContainer.classList.remove("show");
}

function setRemoveToastTimeout(toastContainer: Element, toastDuration = TOAST_DEFAULT_DURATION): number {
    return window.setTimeout(() => {
        removeToast(toastContainer);
    }, toastDuration);
}

/**
 * Shows a toast in given container
 * @param ShowToastParam
 * @param ShowToastParam.text - text to be shown
 * @param ShowToastParam.source - key to indicate where toast is triggered from, helps differentiating toasts with same text and container
 * @param ShowToastParam.container - where toast is rendered
 * @param ShowToastParam.onClick - onClick callback
 */
function showToast({
    text,
    variant,
    source,
    container = document.body,
    component,
    onClick
}: {
    text: string;
    variant?: ToastVariant;
    source: string;
    container?: Element;
    component?: ComponentType<{ children?: string; variant?: ToastVariant; }>;
    onClick?: () => void;
}) {
    let toastContainer = ToastStore.container && ToastStore.container.querySelector(".toastContainer");

    if (ToastStore.visible && !document.body.contains(toastContainer)) {
        ToastStore.visible = false;
    }

    if (ToastStore.visible && toastContainer) {
        // If a toast is currently visible, displaying the same text as the new one and they are from the same source, then current toast's duration is extended
        if (source === ToastStore.source && text === ToastStore.text) {
            window.clearTimeout(ToastStore.removeTimeout!);
            ToastStore.removeTimeout = setRemoveToastTimeout(toastContainer);
            return;
        }

        // If a toast is currently visible but new toast has a different text or from a different source, then current toast is dismissed
        render(null, toastContainer);
        toastContainer.remove();
    }

    toastContainer = document.createElement("div");
    toastContainer.classList.add("toastContainer");

    if (onClick) {
        toastContainer.addEventListener("click", onClick);
        toastContainer.classList.add("clickable");
    }

    container.appendChild(toastContainer);

    render(h(component ?? Toast, { children: text, variant }), toastContainer);

    // Update store for the new toast
    ToastStore.container = container;
    ToastStore.source = source;
    ToastStore.text = text;
    ToastStore.visible = true;

    window.setTimeout(() => {
        toastContainer!.classList.add("show");

        ToastStore.removeTimeout = setRemoveToastTimeout(toastContainer!);
    }, TOAST_SHOW_DELAY);
}

function hideToast() {
    const toastContainer = ToastStore.container &&
        ToastStore.container.querySelector(".toastContainer.show");

    if (!toastContainer) {
        return;
    }

    if (ToastStore.removeTimeout) {
        window.clearTimeout(ToastStore.removeTimeout);
    }

    removeToast(toastContainer);
}

function showToastComponent({ component, container, duration }: {
    component: JSX.Element;
    container: Element;
    duration?: number;
}) {
    let toastContainer = ToastStore.container && ToastStore.container.querySelector(".toastContainer");

    if (ToastStore.visible && !document.body.contains(toastContainer)) {
        ToastStore.visible = false;
    }

    if (ToastStore.visible && toastContainer) {
        render(null, toastContainer);
        toastContainer.remove();
    }

    toastContainer = document.createElement("div");
    toastContainer.classList.add("toastContainer");
    container.appendChild(toastContainer);

    render(component, toastContainer);

    toastContainer.addEventListener("mouseenter", () => {
        window.clearTimeout(ToastStore.removeTimeout!);
    });

    toastContainer.addEventListener("mouseleave", () => {
        ToastStore.removeTimeout = setRemoveToastTimeout(toastContainer!, duration);
    });

    // Update store for the new toast
    ToastStore.text = null;
    ToastStore.source = null;
    ToastStore.container = container;
    ToastStore.visible = true;

    window.setTimeout(() => {
        toastContainer!.classList.add("show");

        ToastStore.removeTimeout = setRemoveToastTimeout(toastContainer!, duration);
    }, TOAST_SHOW_DELAY);
}

function setPendingToast({ text, target, duration }: {
    text: string;
    target: PendingToastTarget;
    duration?: number;
}) {
    const component = h(Toast, { children: text });

    ToastStore.pendingToast = {
        component,
        target,
        duration
    };
}

function setPendingToastComponent({ component, target, duration }: {
    component: JSX.Element;
    target: PendingToastTarget;
    duration?: number;
}) {
    ToastStore.pendingToast = {
        component,
        target,
        duration
    };
}

function showPendingToast({ container, target }: {
    container: Element | null | undefined;
    target: PendingToastTarget;
}) {
    if (!ToastStore.pendingToast) {
        return;
    }

    const {
        component,
        target: pendingTarget,
        duration
    } = ToastStore.pendingToast;

    if (!container || target !== pendingTarget) {
        return;
    }

    showToastComponent({ component, container, duration });
    ToastStore.pendingToast = null;
}

export {
    showToastComponent,
    hideToast,
    setPendingToast,
    setPendingToastComponent,
    showPendingToast,
    PendingToastTarget
};
export default showToast;
